import { OffsetsAndStyles } from 'craft/utils/viewport';

import { Spacing } from './SpacingAirHandler';

interface SpacingMarginStylesProps {
  key: string;
  dom: HTMLElement;
  offsetsAndStyles: OffsetsAndStyles;
  spacing: Spacing;
}

export const getSpacingStyles = ({
  key,
  dom,
  offsetsAndStyles,
  spacing,
}: SpacingMarginStylesProps) => {
  const { offsetWidth, offsetHeight } = dom;

  if (key.includes('padding')) {
    return getPaddingOffsetsAndStyles({
      key,
      data: { ...offsetsAndStyles, ...spacing, offsetWidth, offsetHeight } as GetterProps['data'],
    });
  }

  return getMarginOffsetsAndStyles({
    key,
    data: { ...offsetsAndStyles, ...spacing, offsetWidth, offsetHeight } as GetterProps['data'],
  });
};

interface GetterProps {
  key: string;
  data: OffsetsAndStyles & Spacing & { offsetWidth: number; offsetHeight: number };
}

const getMarginWidth = ({ key, data }: GetterProps): string | number => {
  const { left, documentWidth, documentPadding, marginRight, marginLeft, offsetWidth } = data;

  let marginLeftWidth = marginLeft;
  if (marginLeft === 'auto') {
    marginLeftWidth = `${left - documentPadding.left}px`;
  } else if (marginLeft.toString().includes('%')) {
    marginLeftWidth = `${Math.floor(documentWidth * (parseFloat(marginLeft) / 100))}px`;
  }

  let marginRightWidth = marginRight;
  if (marginRight === 'auto') {
    marginRightWidth = `${documentWidth - offsetWidth - parseFloat(marginLeftWidth)}px`;
  } else if (marginRight.toString().includes('%')) {
    marginRightWidth = `${Math.floor(documentWidth * (parseFloat(marginRight) / 100))}px`;
  }

  if (key === 'marginTop') {
    return `calc(${offsetWidth}px + ${marginLeftWidth} + ${marginRightWidth})`;
  }

  if (key === 'marginRight') {
    return marginRightWidth;
  }

  if (key === 'marginBottom') {
    return `calc(${offsetWidth}px + ${marginLeftWidth} + ${marginRightWidth})`;
  }

  if (key === 'marginLeft') {
    return marginLeftWidth;
  }

  return '0px';
};

const getMarginHeight = ({ key, data }: GetterProps): string | number => {
  const { documentWidth, marginTop, marginBottom, offsetHeight } = data;

  if (key === 'marginTop') {
    return marginTop.toString().includes('%')
      ? `calc(${Math.floor(documentWidth * (parseFloat(marginTop) / 100))}px)`
      : marginTop;
  }

  if (key === 'marginRight' || key === 'marginLeft') {
    return offsetHeight;
  }

  if (key === 'marginBottom') {
    return marginBottom.toString().includes('%')
      ? `calc(${Math.floor(documentWidth * (parseFloat(marginBottom) / 100))}px)`
      : marginBottom;
  }

  return '0px';
};

const getMarginOffsetsAndStyles = ({ key, data }: GetterProps): Record<string, string | number> => {
  const { offsetWidth, offsetHeight } = data;
  const marginTopHeight = getMarginHeight({ key: 'marginTop', data });
  const marginLeftWidth = getMarginWidth({ key: 'marginLeft', data });

  const marginDimensions = {
    width: getMarginWidth({
      key,
      data,
    }),
    height: getMarginHeight({
      key,
      data,
    }),
  };

  if (key === 'marginTop') {
    return {
      left: `calc(${marginLeftWidth} * -1)`,
      top: `calc(${marginTopHeight} * -1)`,
      ...marginDimensions,
    };
  }

  if (key === 'marginRight') {
    return {
      top: 0,
      left: offsetWidth,
      ...marginDimensions,
    };
  }

  if (key === 'marginBottom') {
    return {
      top: offsetHeight,
      left: `calc(${marginLeftWidth} * -1)`,
      ...marginDimensions,
    };
  }

  if (key === 'marginLeft') {
    return {
      top: 0,
      left: `calc(${marginLeftWidth} * -1)`,
      ...marginDimensions,
    };
  }

  return {
    top: 0,
    left: 0,
    ...marginDimensions,
  };
};

const getPaddingWidth = ({ key, data }: GetterProps): string | number => {
  const { documentWidth, offsetWidth, paddingRight, paddingLeft } = data;

  if (key === 'paddingTop') {
    return `${offsetWidth}px`;
  }

  if (key === 'paddingRight') {
    return paddingRight.toString().includes('%')
      ? `calc(${Math.floor(documentWidth * (parseFloat(paddingRight) / 100))}px)`
      : paddingRight;
  }

  if (key === 'paddingBottom') {
    return `${offsetWidth}px`;
  }

  if (key === 'paddingLeft') {
    return paddingLeft.toString().includes('%')
      ? `calc(${Math.floor(documentWidth * (parseFloat(paddingLeft) / 100))}px)`
      : paddingLeft;
  }

  return '0px';
};

const getPaddingHeight = ({ key, data }: GetterProps): string | number => {
  const { offsetHeight, paddingTop, paddingBottom, documentWidth } = data;

  const paddingTopHeight = paddingTop.toString().includes('%')
    ? `${Math.floor(documentWidth * (parseFloat(paddingTop) / 100))}px`
    : paddingTop;

  const paddingBottomHeight = paddingBottom.toString().includes('%')
    ? `${Math.floor(documentWidth * (parseFloat(paddingBottom) / 100))}px`
    : paddingBottom;

  if (key === 'paddingTop') {
    return paddingTopHeight;
  }

  if (key === 'paddingRight' || key === 'paddingLeft') {
    return `calc(${offsetHeight}px - ${paddingTopHeight} - ${paddingBottomHeight})`;
  }

  if (key === 'paddingBottom') {
    return paddingBottomHeight;
  }

  return '0px';
};

const getPaddingOffsetsAndStyles = ({
  key,
  data,
}: GetterProps): Record<string, string | number> => {
  const { offsetWidth, offsetHeight } = data;
  const paddingTopHeight = getPaddingHeight({ key: 'paddingTop', data });
  const paddingBottomHeight = getPaddingHeight({ key: 'paddingBottom', data });
  const paddingRightWidth = getPaddingWidth({ key: 'paddingRight', data });

  const paddingDimensions = {
    width: getPaddingWidth({
      key,
      data,
    }),
    height: getPaddingHeight({
      key,
      data,
    }),
  };

  if (key === 'paddingTop') {
    return {
      top: 0,
      left: 0,
      ...paddingDimensions,
    };
  }

  if (key === 'paddingRight') {
    return {
      top: `${paddingTopHeight}`,
      left: `calc(${offsetWidth}px - ${paddingRightWidth})`,
      ...paddingDimensions,
    };
  }

  if (key === 'paddingBottom') {
    return {
      left: 0,
      top: `calc(${offsetHeight}px - ${paddingBottomHeight})`,
      ...paddingDimensions,
    };
  }

  if (key === 'paddingLeft') {
    return {
      top: `calc(${paddingTopHeight})`,
      left: 0,
      ...paddingDimensions,
    };
  }

  return { top: 0, left: 0, ...paddingDimensions };
};
